import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'

import { TooltipSeeker } from '@ui-seeker/core'
import { dataColors, dsColorNew } from '@palette'

// Three soft tints, as the app's notes: the number in the dark tone of its tint
const TONES = {
	good: { bg: dsColorNew.successBg, fg: dsColorNew.successText },
	mid: { bg: dsColorNew.noteAmberBg, fg: dsColorNew.noteAmber },
	bad: { bg: dsColorNew.dangerBg, fg: dataColors.red700 },
}

const toneOf = fraction => fraction < 1 / 3 ? TONES.good : fraction < 2 / 3 ? TONES.mid : TONES.bad

const shortDate = date => `${String(date).slice(6, 8)}/${String(date).slice(4, 6)}`

// Room a date label needs ("27/10" plus a gap), so a long report labels one cell in N instead of overlapping them
const LABEL_WIDTH = 40

function useWidth(active) {
	const ref = useRef(null)
	const [width, setWidth] = useState(0)
	useEffect(() => {
		if (!ref.current) return
		const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
		observer.observe(ref.current)
		return () => observer.disconnect()
	}, [active])
	return [ref, width]
}

/**
 * One cell per date with the main brand's market rank, tinted green (cheapest third),
 * amber or red (dearest third) relative to the brands quoting that date.
 */
export default function RankStrip({ rows, totalBrands }) {
	const cells = rows.filter(({ rank }) => typeof rank === 'number')
	const [ref, width] = useWidth(cells.length > 0)

	if (!cells.length) return null

	const labelStep = width ? Math.max(1, Math.ceil(LABEL_WIDTH / (width / rows.length))) : 1

	return <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>
		<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, fontWeight: 600 }}>
			Main rank by pickup date
		</Typography>
		<Box ref={ref} sx={{ display: 'flex', gap: .5, mt: 1 }}>
			{rows.map(({ date, rank, brands }, index) => {
				// Normalised against the brands quoting that date: being last of two
				// is the worst spot available, whatever the tracked total is.
				const dayBrands = brands || totalBrands
				const span = dayBrands > 1 ? dayBrands - 1 : 1
				const hasRank = typeof rank === 'number'
				const tone = hasRank && toneOf(Math.min(1, Math.max(0, (rank - 1) / span)))

				return <Box key={date} sx={{ flex: 1, minWidth: 0 }}>
				<TooltipSeeker title={hasRank
					? `${shortDate(date)} — rank ${rank} of ${dayBrands}`
					: `${shortDate(date)} — no rate`}>
					<Box sx={{
						borderRadius: '6px',
						py: .5,
						textAlign: 'center',
						fontSize: 12,
						fontWeight: 700,
						color: hasRank ? tone.fg : dsColorNew.textSubtle,
						backgroundColor: hasRank ? tone.bg : dsColorNew.pillTrackBg,
					}}>
						{hasRank ? rank : '—'}
					</Box>
				</TooltipSeeker>
				<Typography
					variant="caption"
					sx={{ display: 'block', textAlign: 'center', whiteSpace: 'nowrap', color: dsColorNew.textMuted, fontSize: 11, mt: .5,
					      visibility: index % labelStep ? 'hidden' : 'visible' }}>
					{shortDate(date)}
				</Typography>
			</Box>
			})}
		</Box>
	</Box>
}
